import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDndContext,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Archive,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  FileText,
  GripVertical,
  ListOrdered,
  MoreVertical,
  Plus,
  Trash2,
  Trophy,
} from 'lucide-react';
import { motion } from 'motion/react';
import { lazy, Suspense, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { EndSessionDialog } from '@/components/features/sessions/active/EndSessionDialog';
import { PausedSessionBanner } from '@/components/features/sessions/active/PausedSessionBanner';
import { PlayerScoreCard } from '@/components/features/sessions/active/PlayerScoreCard';
import { PlayerScoreCardSkeleton } from '@/components/features/sessions/active/PlayerScoreCardSkeleton';
import { RoundNavigation } from '@/components/features/sessions/active/RoundNavigation';
import { SessionDetailsSheet } from '@/components/features/sessions/active/SessionDetailsSheet';
import { SessionHeader } from '@/components/features/sessions/active/SessionHeader';
import { EventTimeline } from '@/components/features/sessions/history/EventTimeline';
import { FinalScoresSummary } from '@/components/features/sessions/history/FinalScoresSummary';
import { HistoryStatsKpis } from '@/components/features/sessions/history/HistoryStatsKpis';
import { RoundBreakdown } from '@/components/features/sessions/history/RoundBreakdown';
import { StartNewSessionDialog } from '@/components/features/sessions/history/StartNewSessionDialog';
import { WinnerBanner } from '@/components/features/sessions/history/WinnerBanner';
import { SaveTemplateDialog } from '@/components/features/sessions/templates/SaveTemplateDialog';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { PlayerAvatar } from '@/components/shared/PlayerAvatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { getSessionEvents } from '@/db/session-events';
import { useIsMobile } from '@/hooks/use-mobile';
import { useActionErrorHandler } from '@/hooks/useActionErrorHandler';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { handleBackgroundError } from '@/lib/error-handler';
import { fadeIn, slideUp, staggerConfig, transitions } from '@/lib/motion-config';
import { toastError, toastPromise } from '@/lib/toast-helpers';
import { cn } from '@/lib/utils';
import {
  parseRoundId,
  parseSessionId,
  safeParseSessionId,
  type PlayerId,
  type Round,
  type Session,
  type SessionEvent,
} from '@/schemas';
import type { SessionStore } from '@/stores';
import { useSessionStore, getActivePlayers, getCurrentRound, getActiveRounds } from '@/stores';
import { getTotalScore } from '@/stores/scoring';

const HistoryCharts = lazy(() =>
  import('@/components/features/sessions/history/HistoryCharts').then((m) => ({
    default: m.HistoryCharts,
  })),
);

export const Route = createFileRoute('/sessions/$sessionId')({
  loader: ({ params }) => {
    const parseResult = safeParseSessionId(params.sessionId);
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: SessionPage,
});

function formatDuration(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const weeks = Math.floor(sec / (7 * 24 * 3600));
  const days = Math.floor((sec % (7 * 24 * 3600)) / (24 * 3600));
  const h = Math.floor((sec % (24 * 3600)) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;

  const parts: string[] = [];
  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 && parts.length === 0) parts.push(`${s}s`);

  return parts.join(' ') || '0s';
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface FinishedSessionViewProps {
  readonly session: Session;
  readonly activePlayers: ReturnType<typeof getActivePlayers>;
  readonly t: ReturnType<typeof useTranslation<'sessions'>>['t'];
  readonly navigate: ReturnType<typeof useNavigate>;
}

type FinishedSectionKey = 'stats' | 'charts' | 'rounds' | 'timeline';

interface FinishedSectionConfig {
  readonly key: FinishedSectionKey;
  readonly label: string;
  readonly content: ReactNode;
}

function FinishedSessionView({ session, activePlayers, t, navigate }: FinishedSessionViewProps) {
  const [events, setEvents] = useState<SessionEvent[] | undefined>();
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [isStartNewSessionDialogOpen, setIsStartNewSessionDialogOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<FinishedSectionKey>('stats');
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const { archiveSession, deleteSession } = useSessionStore();

  const sessionDuration =
    session.endedAt && session.startedAt ? session.endedAt - session.startedAt : 0;

  // Fetch events for finished sessions
  useEffect(() => {
    let cancelled = false;
    setIsLoadingEvents(true);
    void (async () => {
      try {
        const evs = await getSessionEvents(session.id);
        if (!cancelled) {
          setEvents(evs);
        }
      } catch (error) {
        handleBackgroundError(error, { operation: 'fetchSessionEvents', sessionId: session.id });
        if (!cancelled) {
          setEvents([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEvents(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.id]);

  const activeRounds = useMemo(() => getActiveRounds(session), [session]);
  const hasCharts = useMemo(
    () => activeRounds.length >= 3 && activePlayers.length > 0,
    [activeRounds.length, activePlayers.length],
  );

  const sections = useMemo<FinishedSectionConfig[]>(() => {
    const baseSections: (FinishedSectionConfig | undefined)[] = [
      {
        key: 'stats',
        label: t('history.sections.stats', 'Stats'),
        content: (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{t('history.sections.stats', 'Stats')}</h2>
            <HistoryStatsKpis session={session} events={events ?? []} isLoading={isLoadingEvents} />
          </div>
        ),
      },
      hasCharts
        ? {
            key: 'charts' as const,
            label: t('history.sections.charts', 'Charts'),
            content: (
              <Suspense
                fallback={
                  <Card>
                    <CardContent className="py-8">
                      <Skeleton className="h-64 w-full" />
                    </CardContent>
                  </Card>
                }
              >
                <HistoryCharts session={session} events={events ?? []} />
              </Suspense>
            ),
          }
        : undefined,
      {
        key: 'rounds',
        label: t('history.sections.rounds', 'Rounds'),
        content: <RoundBreakdown session={session} />,
      },
      {
        key: 'timeline',
        label: t('history.sections.timeline', 'Timeline'),
        content: (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{t('history.sections.timeline', 'Timeline')}</h2>
            {isLoadingEvents ? (
              <Card>
                <CardContent className="py-8">
                  <div className="flex items-center justify-center">
                    <p className="text-sm text-muted-foreground">
                      {t('history.loadingEvents', 'Loading events...')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <EventTimeline session={session} events={events ?? []} isFinished={true} />
            )}
          </div>
        ),
      },
    ];

    return baseSections.filter(
      (section): section is FinishedSectionConfig => section !== undefined,
    );
  }, [events, hasCharts, isLoadingEvents, session, t]);

  const handleCreateTemplate = () => {
    setIsSaveTemplateOpen(true);
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await toastPromise(archiveSession(), {
        loading: t('history.archive.loading', 'Archiving...'),
        success: t('history.archive.success', 'Session archived'),
        error: (err: unknown) =>
          err instanceof Error
            ? err.message
            : t('history.archive.error', 'Failed to archive session'),
      });
      void navigate({ to: '/' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('history.archive.error');
      toastError(errorMessage);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await toastPromise(deleteSession(), {
        loading: t('history.delete.loading', 'Deleting...'),
        success: t('history.delete.success', 'Session deleted'),
        error: (err: unknown) =>
          err instanceof Error
            ? err.message
            : t('history.delete.error', 'Failed to delete session'),
      });
      void navigate({ to: '/' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('history.delete.error');
      toastError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!sections.some((section) => section.key === mobileSection)) {
      setMobileSection(sections[0]?.key ?? 'stats');
    }
  }, [mobileSection, sections]);

  return (
    <ErrorBoundary>
      <div className="w-full max-w-6xl mx-auto space-y-6 px-3 py-6 sm:px-4 md:py-8">
        {/* Hero Section */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <CardTitle className="text-2xl font-bold sm:text-3xl">
                  {session.name || t('title', 'Session')}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground sm:text-base">
                  {t('game', 'Game')}
                </CardDescription>
              </div>
              <div className="flex w-full flex-wrap gap-2 md:w-auto md:justify-end">
                <Button
                  onClick={() => {
                    setIsStartNewSessionDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('history.startNewSession', 'Start New Session')}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={t('history.more', 'More options')}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleCreateTemplate}
                      disabled={isDeleting || isArchiving}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      {t('history.createTemplate', 'Create Template')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        void handleArchive();
                      }}
                      disabled={isDeleting || isArchiving}
                    >
                      <Archive className="mr-2 h-4 w-4" />
                      {t('history.archive', 'Archive')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setIsDeleteDialogOpen(true)}
                      disabled={isDeleting || isArchiving}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('history.delete', 'Delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {session.startedAt ? (
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('history.metadata.started', 'Started')}
                  </p>
                  <p className="text-sm font-medium">{formatDate(session.startedAt)}</p>
                </div>
              ) : undefined}
              {session.endedAt ? (
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('history.metadata.ended', 'Ended')}
                  </p>
                  <p className="text-sm font-medium">{formatDate(session.endedAt)}</p>
                </div>
              ) : undefined}
              {sessionDuration > 0 ? (
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('history.metadata.duration', 'Duration')}
                  </p>
                  <p className="text-sm font-medium">{formatDuration(sessionDuration)}</p>
                </div>
              ) : undefined}
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('history.metadata.players', 'Players')} ({activePlayers.length})
                </p>
                <div className="flex -space-x-2 mt-1">
                  {activePlayers.map((player) => (
                    <PlayerAvatar
                      key={player.id}
                      player={player}
                      color={player.color}
                      size="sm"
                      className="relative border-2 border-background"
                      fallbackClassName="text-xs"
                    />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <WinnerBanner session={session} />

        <FinalScoresSummary session={session} />

        <div className="space-y-4 sm:space-y-6">
          {sections.length > 1 ? (
            <div className="sm:hidden">
              <ToggleGroup
                type="single"
                value={mobileSection}
                className="grid grid-cols-2 gap-2"
                onValueChange={(value) => {
                  if (value) {
                    setMobileSection(value as FinishedSectionKey);
                  }
                }}
                aria-label={t('history.sections.toggleLabel', 'Select session section')}
              >
                {sections.map((section) => (
                  <ToggleGroupItem
                    key={section.key}
                    value={section.key}
                    className="text-xs font-medium data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
                  >
                    {section.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          ) : undefined}

          {sections.map((section) => (
            <section
              key={section.key}
              className={cn(
                'sm:block',
                mobileSection === section.key ? 'block' : 'hidden sm:block',
              )}
            >
              {section.content}
            </section>
          ))}
        </div>
        <StartNewSessionDialog
          open={isStartNewSessionDialogOpen}
          onOpenChange={setIsStartNewSessionDialogOpen}
          session={session}
        />
        <SaveTemplateDialog
          open={isSaveTemplateOpen}
          onOpenChange={setIsSaveTemplateOpen}
          session={session}
        />
        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title={t('history.deleteConfirm.title', 'Delete session?')}
          description={t('history.deleteConfirm.description', 'This action cannot be undone.')}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          confirmLabel={t('history.delete', 'Delete')}
        />
      </div>
    </ErrorBoundary>
  );
}

interface PlayersGridProps {
  readonly displayedPlayers: ReturnType<typeof getActivePlayers>;
  readonly activeSessionTyped: Session;
  readonly displayRound: Round | undefined;
  readonly viewedRoundIndex?: number;
  readonly currentRoundIndex?: number;
  readonly selectedRoundId?: string;
  readonly currentRound?: Round;
  readonly onScoreChange: (playerId: PlayerId, delta: number) => Promise<void>;
  readonly onScoreSet?: (playerId: PlayerId, value: number, roundId?: string) => Promise<void>;
  readonly onRemovePlayer?: (playerId: PlayerId) => Promise<void>;
  readonly isPaused: boolean;
  readonly playerRankings: {
    readonly leaderIds: Set<PlayerId>;
    readonly lastPlaceIds: Set<PlayerId>;
    readonly tiedPlayerIds: Set<PlayerId>;
    readonly positions: Map<PlayerId, number>;
    readonly shouldShowPositions: boolean;
  };
  readonly isDragEnabled: boolean;
}

function PlayersGrid({
  displayedPlayers,
  activeSessionTyped,
  displayRound,
  viewedRoundIndex,
  currentRoundIndex,
  selectedRoundId,
  currentRound,
  onScoreChange,
  onScoreSet,
  onRemovePlayer,
  isPaused,
  playerRankings,
  isDragEnabled,
}: PlayersGridProps) {
  const { active } = useDndContext();
  const isAnyDragging = active !== null;
  // Memoize activeRounds calculation to avoid recalculating in map
  const activeRounds = useMemo(() => getActiveRounds(activeSessionTyped), [activeSessionTyped]);
  const roundsCount = activeRounds.length;

  return (
    <motion.div
      layout={!isAnyDragging}
      className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      transition={transitions.springGentle}
      style={isAnyDragging ? { willChange: 'auto' } : undefined}
    >
      {displayedPlayers.map((player) => {
        const position = playerRankings.shouldShowPositions
          ? playerRankings.positions.get(player.id)
          : undefined;
        return (
          <SortablePlayerCard
            key={player.id}
            player={player}
            session={activeSessionTyped}
            round={displayRound}
            viewedRoundIndex={viewedRoundIndex}
            currentRoundIndex={currentRoundIndex}
            isViewingHistoricalRound={
              selectedRoundId !== undefined &&
              currentRound !== undefined &&
              selectedRoundId !== currentRound.id
            }
            onScoreChange={onScoreChange}
            onScoreSet={onScoreSet}
            onRemovePlayer={onRemovePlayer}
            isPaused={isPaused}
            isLeader={playerRankings.shouldShowPositions && playerRankings.leaderIds.has(player.id)}
            isLastPlace={
              playerRankings.shouldShowPositions && playerRankings.lastPlaceIds.has(player.id)
            }
            isTied={
              playerRankings.shouldShowPositions && playerRankings.tiedPlayerIds.has(player.id)
            }
            position={position}
            roundsCount={roundsCount}
            isDragEnabled={isDragEnabled}
            enableLayoutAnimation={true}
          />
        );
      })}
    </motion.div>
  );
}

interface SortablePlayerCardProps {
  readonly player: ReturnType<typeof getActivePlayers>[number];
  readonly session: Session;
  readonly round: Round | undefined;
  readonly viewedRoundIndex?: number;
  readonly currentRoundIndex?: number;
  readonly isViewingHistoricalRound?: boolean;
  readonly onScoreChange: (playerId: PlayerId, delta: number) => Promise<void>;
  readonly onScoreSet?: (playerId: PlayerId, value: number, roundId?: string) => Promise<void>;
  readonly onRemovePlayer?: (playerId: PlayerId) => Promise<void>;
  readonly isPaused?: boolean;
  readonly isLeader?: boolean;
  readonly isLastPlace?: boolean;
  readonly isTied?: boolean;
  readonly position?: number;
  readonly roundsCount?: number;
  readonly isDragEnabled: boolean;
  readonly enableLayoutAnimation?: boolean;
}

function SortablePlayerCard({
  player,
  session,
  round,
  viewedRoundIndex,
  currentRoundIndex,
  isViewingHistoricalRound,
  onScoreChange,
  onScoreSet,
  onRemovePlayer,
  isPaused,
  isLeader,
  isLastPlace,
  isTied,
  position,
  roundsCount,
  isDragEnabled,
  enableLayoutAnimation = false,
}: SortablePlayerCardProps) {
  const isMobile = useIsMobile();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: player.id,
    disabled: !isDragEnabled,
  });
  const { active } = useDndContext();
  const isAnyDragging = active !== null;

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition: enableLayoutAnimation ? undefined : transition, // Disable dnd-kit transition when layout animation is enabled
      opacity: isDragging ? 0.5 : 1,
      willChange: isDragging ? 'transform' : 'auto',
    }),
    [transform, transition, enableLayoutAnimation, isDragging],
  );

  // On mobile, only apply drag listeners to the handle; on desktop, apply to entire card
  const cardListeners = useMemo(() => (isMobile ? {} : listeners), [isMobile, listeners]);
  const handleListeners = useMemo(() => (isMobile ? listeners : {}), [isMobile, listeners]);

  const cardContent = (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...cardListeners}
      className={isDragEnabled && !isMobile ? 'cursor-grab active:cursor-grabbing group' : ''}
    >
      <div className="relative">
        {isDragEnabled && (
          <div
            {...handleListeners}
            className={cn(
              'absolute z-50 flex items-center justify-center rounded-md',
              isMobile
                ? 'left-2 top-[60%] -translate-y-1/2 opacity-100 pointer-events-auto cursor-grab active:cursor-grabbing w-10 h-14 touch-none select-none bg-transparent'
                : 'left-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 w-5 h-10 bg-background/95 border border-border/50 shadow-sm transition-all duration-200',
            )}
            style={isMobile ? { touchAction: 'none', userSelect: 'none' } : undefined}
            aria-label="Drag to reorder"
            role="button"
            tabIndex={isMobile ? 0 : -1}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        <PlayerScoreCard
          player={player}
          session={session}
          round={round}
          viewedRoundIndex={viewedRoundIndex}
          currentRoundIndex={currentRoundIndex}
          isViewingHistoricalRound={isViewingHistoricalRound}
          onScoreChange={onScoreChange}
          onScoreSet={onScoreSet}
          onRemovePlayer={onRemovePlayer}
          isPaused={isPaused}
          isLeader={isLeader}
          isLastPlace={isLastPlace}
          isTied={isTied}
          position={position}
          roundsCount={roundsCount}
          isDragging={isAnyDragging}
        />
      </div>
    </div>
  );

  // Disable layout animation during drag for better performance
  if (enableLayoutAnimation && !isAnyDragging) {
    return (
      <motion.div
        layout
        layoutId={`player-score-card-${player.id}`}
        transition={transitions.springGentle}
        style={{ willChange: 'transform' }}
      >
        {cardContent}
      </motion.div>
    );
  }

  return cardContent;
}

// Helper to calculate player positions based on scores
function calculatePlayerPositions(
  playerScores: Array<{ id: PlayerId; score: number }>,
): Map<PlayerId, number> {
  const positions = new Map<PlayerId, number>();
  let currentPosition = 1;
  for (let i = 0; i < playerScores.length; i += 1) {
    const currentPlayer = playerScores[i];
    if (!currentPlayer) continue;

    // If this is not the first player and score differs from previous, increment position
    if (i > 0 && playerScores[i - 1] && currentPlayer.score < playerScores[i - 1].score) {
      currentPosition = i + 1;
    }

    positions.set(currentPlayer.id, currentPosition);
  }
  return positions;
}

// Helper to find tied players (players sharing the same score)
function findTiedPlayers(playerScores: Array<{ id: PlayerId; score: number }>): Set<PlayerId> {
  const scoreCount = new Map<number, number>();
  for (const { score } of playerScores) {
    scoreCount.set(score, (scoreCount.get(score) ?? 0) + 1);
  }
  const tiedPlayerIds = new Set<PlayerId>();
  for (const { id, score } of playerScores) {
    if ((scoreCount.get(score) ?? 0) > 1) {
      tiedPlayerIds.add(id);
    }
  }
  return tiedPlayerIds;
}

function SessionPage() {
  const { sessionId } = Route.useParams();
  const { t } = useTranslation('sessions');
  const { t: tCommon } = useTranslation('common');
  const { shouldAnimate, routeKey } = useRouteAnimation();
  const storeSelection = useSessionStore(
    useShallow((state) => ({
      loadSession: state.loadSession,
      addScore: state.addScore,
      setScore: state.setScore,
      completeRound: state.completeRound,
      startRound: state.startRound,
      addRoundNote: state.addRoundNote,
      addRoundTag: state.addRoundTag,
      endSession: state.endSession,
      setSessionWinner: state.setSessionWinner,
      undo: state.undo,
      redo: state.redo,
      reorderPlayers: state.reorderPlayers,
      activeSession: state.activeSession,
      isLoading: state.isLoading,
      error: state.error,
      canUndo: state.canUndo,
      canRedo: state.canRedo,
    })),
  ) as {
    loadSession: SessionStore['loadSession'];
    addScore: SessionStore['addScore'];
    setScore: SessionStore['setScore'];
    completeRound: SessionStore['completeRound'];
    startRound: SessionStore['startRound'];
    addRoundNote: SessionStore['addRoundNote'];
    addRoundTag: SessionStore['addRoundTag'];
    endSession: SessionStore['endSession'];
    setSessionWinner: SessionStore['setSessionWinner'];
    undo: SessionStore['undo'];
    redo: SessionStore['redo'];
    reorderPlayers: SessionStore['reorderPlayers'];
    activeSession: SessionStore['activeSession'];
    isLoading: SessionStore['isLoading'];
    error: SessionStore['error'];
    canUndo: SessionStore['canUndo'];
    canRedo: SessionStore['canRedo'];
    archiveSession: SessionStore['archiveSession'];
    deleteSession: SessionStore['deleteSession'];
  };

  const loadSession = storeSelection.loadSession;
  const addScore = storeSelection.addScore;
  const setScore = storeSelection.setScore;
  const completeRound = storeSelection.completeRound;
  const startRound = storeSelection.startRound;
  const addRoundNote = storeSelection.addRoundNote;
  const addRoundTag = storeSelection.addRoundTag;
  const endSession = storeSelection.endSession;
  const setSessionWinner = storeSelection.setSessionWinner;
  const undo = storeSelection.undo;
  const redo = storeSelection.redo;
  const reorderPlayers = storeSelection.reorderPlayers;
  const activeSessionTyped: Session | undefined = storeSelection.activeSession;
  const isLoading = storeSelection.isLoading;
  const sessionError = storeSelection.error;
  const canUndo = storeSelection.canUndo;
  const canRedo = storeSelection.canRedo;
  const { actionError, handleAction, isPending } = useActionErrorHandler();
  const [isEndSessionOpen, setIsEndSessionOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<string | undefined>();
  const brandedSessionId = useMemo(() => parseSessionId(sessionId), [sessionId]);
  const activePlayers = useMemo(() => getActivePlayers(activeSessionTyped), [activeSessionTyped]);
  const currentRound = useMemo(() => getCurrentRound(activeSessionTyped), [activeSessionTyped]);
  const activeRounds = useMemo(() => getActiveRounds(activeSessionTyped), [activeSessionTyped]);

  // Determine which round to display (selected or current)
  // Automatically falls back to currentRound if selectedRoundId is invalid
  const displayRound = useMemo(() => {
    if (!selectedRoundId || !activeSessionTyped) return currentRound;

    // Validate selected round still exists and isn't the current round
    const roundExists = activeRounds.some((r) => r.id === selectedRoundId);
    const currentRoundId = currentRound?.id ?? '';
    const isCurrentRoundSelected = String(currentRoundId) === String(selectedRoundId);

    if (!roundExists || isCurrentRoundSelected) {
      return currentRound;
    }

    const found = activeRounds.find((r) => r.id === selectedRoundId);
    return found ?? currentRound;
  }, [selectedRoundId, activeRounds, currentRound, activeSessionTyped]);

  // Compute viewedRoundIndex (0-based) for the selected round
  // undefined means viewing current round
  const viewedRoundIndex = useMemo(() => {
    if (!displayRound || !currentRound || !activeSessionTyped) return;
    if (displayRound.id === currentRound.id) return;

    const sortedRounds = activeRounds.toSorted((a, b) => a.number - b.number);
    const index = sortedRounds.findIndex((r) => r.id === displayRound.id);
    return index === -1 ? undefined : index;
  }, [displayRound, currentRound, activeRounds, activeSessionTyped]);

  // Compute currentRoundIndex (0-based)
  const currentRoundIndex = useMemo(() => {
    if (!currentRound || !activeSessionTyped) return;
    const sortedRounds = activeRounds.toSorted((a, b) => a.number - b.number);
    const index = sortedRounds.findIndex((r) => r.id === currentRound.id);
    return index === -1 ? undefined : index;
  }, [currentRound, activeRounds, activeSessionTyped]);

  // Clear invalid selection when rounds or current round changes
  useEffect(() => {
    if (!selectedRoundId) return;

    const roundExists = activeRounds.some((r) => r.id === selectedRoundId);
    const currentRoundId = currentRound?.id ?? '';
    const isCurrentRoundSelected = String(currentRoundId) === String(selectedRoundId);

    if (!roundExists || isCurrentRoundSelected) {
      setSelectedRoundId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only clear invalid selections, not all selections
  }, [activeRounds.length, currentRound?.id]);

  // Calculate player rankings for leader/last place indicators and positions
  const playerRankings = useMemo(() => {
    const emptyRankings = {
      leaderIds: new Set<PlayerId>(),
      lastPlaceIds: new Set<PlayerId>(),
      tiedPlayerIds: new Set<PlayerId>(),
      positions: new Map<PlayerId, number>(),
      shouldShowPositions: false,
    };

    if (!activeSessionTyped || activePlayers.length === 0) {
      return emptyRankings;
    }

    // Calculate all player scores
    const playerScores = activePlayers.map((player) => ({
      id: player.id,
      score: getTotalScore(activeSessionTyped, player.id),
    }));

    // Sort by score (descending)
    playerScores.sort((a, b) => b.score - a.score);

    if (playerScores.length === 0) {
      return emptyRankings;
    }

    // Check if all players have 0 score - don't show positions in this case
    const allZero = playerScores.every((p) => p.score === 0);
    const shouldShowPositions = !allZero;

    // Find leader(s) - highest score(s)
    const maxScore = playerScores[0]?.score ?? -Infinity;
    const leaderIds = new Set<PlayerId>(
      playerScores.filter((p) => p.score === maxScore).map((p) => p.id),
    );

    // Find last place(s) - lowest score(s)
    const minScore = playerScores.at(-1)?.score ?? Infinity;
    const lastPlaceIds = new Set<PlayerId>(
      playerScores.filter((p) => p.score === minScore).map((p) => p.id),
    );

    // Calculate positions and tied players only if we're showing positions
    const positions = shouldShowPositions
      ? calculatePlayerPositions(playerScores)
      : new Map<PlayerId, number>();
    const tiedPlayerIds = shouldShowPositions ? findTiedPlayers(playerScores) : new Set<PlayerId>();

    return { leaderIds, lastPlaceIds, tiedPlayerIds, positions, shouldShowPositions };
  }, [activeSessionTyped, activePlayers]);

  const [isSessionDetailsOpen, setIsSessionDetailsOpen] = useState(false);
  const [sessionDetailsInitialTab, setSessionDetailsInitialTab] = useState<
    'overview' | 'history' | 'stats' | 'rounds' | undefined
  >();
  const [sessionDetailsInitialRoundId, setSessionDetailsInitialRoundId] = useState<
    string | undefined
  >();
  type PlayerSortCriterion = 'position' | 'score';
  type PlayerSortDirection = 'asc' | 'desc';
  const [sortCriterion, setSortCriterion] = useState<PlayerSortCriterion>('position');
  const [sortDirection, setSortDirection] = useState<PlayerSortDirection>('asc');
  const [isViewOptionsOpen, setIsViewOptionsOpen] = useState(false);
  const viewOptionsContentId = useId();
  const navigate = useNavigate();

  const displayedPlayers = useMemo(() => {
    if (sortCriterion === 'position') {
      if (sortDirection === 'asc') {
        return activePlayers;
      }
      return activePlayers.toReversed();
    }

    if (!activeSessionTyped) {
      return activePlayers;
    }

    const seatOrder = new Map<PlayerId, number>(
      activePlayers.map((player, index) => [player.id, index]),
    );
    const scoreCache = new Map<PlayerId, number>();
    const getScore = (playerId: PlayerId) => {
      const cachedScore = scoreCache.get(playerId);
      if (cachedScore !== undefined) {
        return cachedScore;
      }
      const total = getTotalScore(activeSessionTyped, playerId);
      scoreCache.set(playerId, total);
      return total;
    };

    return activePlayers.toSorted((playerA, playerB) => {
      const aScore = getScore(playerA.id);
      const bScore = getScore(playerB.id);
      if (aScore !== bScore) {
        return sortDirection === 'asc' ? aScore - bScore : bScore - aScore;
      }
      const aOrder = seatOrder.get(playerA.id) ?? 0;
      const bOrder = seatOrder.get(playerB.id) ?? 0;
      return aOrder - bOrder;
    });
  }, [activePlayers, activeSessionTyped, sortCriterion, sortDirection]);

  const handleOpenSessionDetails = (tab?: string, roundId?: string) => {
    if (tab === 'overview' || tab === 'history' || tab === 'stats' || tab === 'rounds') {
      setSessionDetailsInitialTab(tab);
    }
    if (roundId) {
      setSessionDetailsInitialRoundId(roundId);
    }
    setIsSessionDetailsOpen(true);
  };

  const sessionTitle = useMemo(() => {
    if (!activeSessionTyped) return t('title');
    return activeSessionTyped.name || t('title');
  }, [activeSessionTyped, t]);

  useDocumentTitle(`${sessionTitle} - ${tCommon('app.name', 'One Score')}`);

  // Drag and drop setup - must be called before any early returns (React Hooks rule)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    loadSession(brandedSessionId).catch(() => {
      // Error is handled by store - no-op catch
    });
  }, [brandedSessionId, loadSession]);

  const handleUndoClick = async () => {
    await handleAction(async () => undo(), t('undo.error', 'Unable to undo the last action.'));
  };

  const handleRedoClick = async () => {
    await handleAction(async () => redo(), t('redo.error', 'Unable to redo the action.'));
  };

  if (isLoading) {
    return (
      <ErrorBoundary>
        <div className="w-full max-w-6xl mx-auto space-y-6 px-4 py-6 md:py-8">
          {/* SessionHeader skeleton */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-8 w-48" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-9 w-9 rounded" />
              <Skeleton className="h-9 w-9 rounded" />
            </div>
          </div>

          {/* Player cards grid skeleton */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <PlayerScoreCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (sessionError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">{t('errorLoading.title', 'Something went wrong')}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t('errorLoading.description', 'Unable to load this session. Please try again.')}
        </p>
        <Link
          to="/"
          className="inline-block rounded-md bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t('errorLoading.backToHome', 'Back to Home')}
        </Link>
      </div>
    );
  }

  if (!activeSessionTyped) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">{t('notFound', 'Session not found')}</p>
      </div>
    );
  }

  // Allow finished sessions to be viewed even if all players are archived
  // Only show "missing data" for active sessions that truly have no data
  const isFinished = activeSessionTyped?.status === 'finished';
  const hasHistoricalData =
    activeSessionTyped?.rounds.some(
      (round) => !round.deletedAt && Object.keys(round.scores).length > 0,
    ) ?? false;

  // For finished sessions with historical data, always allow viewing (even if players archived)
  // For active sessions: require activePlayers (rounds can be empty after restart)
  // For finished sessions without data: require activePlayers
  const hasRequiredData = activePlayers.length > 0;
  const canViewFinished = isFinished && hasHistoricalData;

  if (!canViewFinished && !hasRequiredData) {
    // Log detailed error information for debugging and send to Sentry in production
    const errorContext = {
      operation: 'sessionMissingData',
      sessionId: brandedSessionId,
      sessionStatus: activeSessionTyped?.status,
      isFinished,
      hasHistoricalData,
      hasCurrentRound: !!currentRound,
      activePlayersCount: activePlayers.length,
      totalRounds: activeSessionTyped?.rounds.length ?? 0,
      roundsWithScores: activeSessionTyped?.rounds.filter(
        (round) => !round.deletedAt && Object.keys(round.scores).length > 0,
      ).length,
      totalPlayers: activeSessionTyped?.players?.length ?? 0,
      allPlayerIds: activeSessionTyped?.players?.map((p) => p.id) ?? [],
      allRoundIds: activeSessionTyped?.rounds?.map((r) => r.id) ?? [],
    };

    handleBackgroundError(
      new Error(
        `Session is missing required data: hasCurrentRound=${!!currentRound}, activePlayersCount=${activePlayers.length}, totalRounds=${activeSessionTyped?.rounds.length ?? 0}, isFinished=${isFinished}, hasHistoricalData=${hasHistoricalData}`,
      ),
      errorContext,
      'error',
    );

    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">{t('missingData', 'Session is missing data')}</p>
      </div>
    );
  }

  const handleScoreChange = async (playerId: PlayerId, delta: number) => {
    if (!currentRound) {
      return;
    }
    await addScore(playerId, currentRound.id, delta);
  };

  const handleScoreSet = async (playerId: PlayerId, value: number, roundId?: string) => {
    const targetRoundId = roundId ? parseRoundId(roundId) : currentRound?.id;

    if (!targetRoundId) {
      return;
    }
    await setScore(playerId, targetRoundId, value);
  };

  const handleRemovePlayer = async (playerId: PlayerId) => {
    const removePlayer = useSessionStore.getState().removePlayer;
    await handleAction(
      async () => removePlayer(playerId),
      t('controls.removePlayerDialog.error', 'Failed to remove player'),
    );
  };

  const handleNextRound = async () => {
    if (!currentRound) {
      return;
    }
    await handleAction(
      async () => {
        // Complete current round if not already completed
        if (!currentRound.completedAt) {
          const brandedRoundId = parseRoundId(currentRound.id);
          await completeRound(brandedRoundId);
        }
        // Start next round
        await startRound();
      },
      t('startNextRound.error', 'Unable to start the next round.'),
    );
  };

  const handleEndSession = (): void => {
    setIsEndSessionOpen(true);
  };

  const handleCompleteRound = async (roundId: string) => {
    const brandedRoundId = parseRoundId(roundId);
    await completeRound(brandedRoundId);
  };

  const handleAddRoundNote = async (roundId: string, note: string) => {
    const brandedRoundId = parseRoundId(roundId);
    await addRoundNote(brandedRoundId, note);
  };

  const handleAddRoundTag = async (roundId: string, tag: string) => {
    const brandedRoundId = parseRoundId(roundId);
    await addRoundTag(brandedRoundId, tag);
  };

  const isPaused = activeSessionTyped.status === 'paused';
  const hasRounds = activeSessionTyped.gameConfigSnapshot.hasRounds === true;
  // At this point, activeSessionTyped is guaranteed to exist (checked by guard above)
  const isFinishedSession = activeSessionTyped.status === 'finished';

  // Only enable drag when sorting by position (ascending), session is not finished, and there's more than one player
  const isDragEnabled =
    !isFinishedSession &&
    sortCriterion === 'position' &&
    sortDirection === 'asc' &&
    activePlayers.length > 1;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !activeSessionTyped || !isDragEnabled) {
      return;
    }

    // When drag is enabled, displayedPlayers should match activePlayers order
    // (sortCriterion === 'position' && sortDirection === 'asc')
    const oldIndex = displayedPlayers.findIndex((player) => player.id === active.id);
    const newIndex = displayedPlayers.findIndex((player) => player.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder the displayed players array
    const reorderedDisplayed = arrayMove(displayedPlayers, oldIndex, newIndex);
    const newPlayerIds = reorderedDisplayed.map((player) => player.id);

    // Validate all active players are included
    const activePlayerIds = new Set(activePlayers.map((p) => p.id));
    const providedPlayerIds = new Set(newPlayerIds);

    if (activePlayerIds.size !== providedPlayerIds.size) {
      return; // Silently fail if player count mismatch
    }

    // Fire and forget - don't await to avoid promise return issue
    void handleAction(
      async () => reorderPlayers(newPlayerIds),
      t('active.reorder.error', 'Failed to reorder players'),
    );
  };

  // For finished sessions, show history view
  if (isFinished && activeSessionTyped) {
    return (
      <ErrorBoundary>
        <motion.div
          key={`finished-session-${routeKey}`}
          variants={fadeIn}
          initial={shouldAnimate ? 'initial' : false}
          animate="animate"
        >
          <FinishedSessionView
            session={activeSessionTyped}
            activePlayers={activePlayers}
            t={t}
            navigate={navigate}
          />
        </motion.div>
      </ErrorBoundary>
    );
  }

  // Handle case where session has no rounds (e.g., after restart)
  // Allow user to start the first round
  if (!currentRound) {
    const handleStartFirstRound = async () => {
      await handleAction(
        async () => startRound(),
        t('startNextRound.error', 'Unable to start the round.'),
      );
    };

    return (
      <ErrorBoundary>
        <div className="w-full max-w-6xl mx-auto space-y-6 px-4 py-6 md:py-8">
          <SessionHeader
            session={activeSessionTyped}
            sessionId={sessionId}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => {
              void handleUndoClick();
            }}
            onRedo={() => {
              void handleRedoClick();
            }}
            onOpenSessionDetails={handleOpenSessionDetails}
            onEndSession={handleEndSession}
            onEndSessionConfirm={endSession}
            onSetWinner={setSessionWinner}
            onToggleViewOptions={() => {
              setIsViewOptionsOpen((prev) => !prev);
            }}
            isViewOptionsOpen={isViewOptionsOpen}
            viewOptionsContentId={viewOptionsContentId}
          />

          {actionError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {actionError}
            </div>
          )}

          {isPaused && (
            <PausedSessionBanner
              session={activeSessionTyped}
              onResume={() => {
                const resumeSession = useSessionStore.getState().resumeSession;
                void handleAction(
                  async () => resumeSession(),
                  t('controls.resumeError', 'Failed to resume session'),
                );
              }}
              isLoading={isPending}
            />
          )}

          <Card className="w-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Trophy className="mb-4 h-12 w-12 text-muted-foreground" />
              <CardTitle className="mb-2 text-center">
                {t('active.noRounds.title', 'No rounds started yet')}
              </CardTitle>
              <CardDescription className="mb-6 text-center">
                {t(
                  'active.noRounds.description',
                  'Start the first round to begin tracking scores for this session.',
                )}
              </CardDescription>
              <Button
                onClick={() => {
                  void handleStartFirstRound();
                }}
                disabled={isPending || isPaused}
                size="lg"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('active.noRounds.startRound', 'Start First Round')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <motion.div
        key={`session-active-${routeKey}`}
        className="w-full max-w-6xl mx-auto space-y-6 px-4 py-6 md:py-8"
        variants={slideUp}
        initial={shouldAnimate ? 'initial' : false}
        animate="animate"
        transition={staggerConfig.container}
      >
        <motion.div variants={slideUp}>
          <SessionHeader
            session={activeSessionTyped}
            sessionId={sessionId}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => {
              void handleUndoClick();
            }}
            onRedo={() => {
              void handleRedoClick();
            }}
            onOpenSessionDetails={handleOpenSessionDetails}
            onEndSession={handleEndSession}
            onEndSessionConfirm={endSession}
            onSetWinner={setSessionWinner}
            onToggleViewOptions={() => {
              setIsViewOptionsOpen((prev) => !prev);
            }}
            isViewOptionsOpen={isViewOptionsOpen}
            viewOptionsContentId={viewOptionsContentId}
          />
        </motion.div>

        {actionError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {actionError}
          </div>
        )}

        {isPaused && (
          <PausedSessionBanner
            session={activeSessionTyped}
            onResume={() => {
              const resumeSession = useSessionStore.getState().resumeSession;
              void handleAction(
                async () => resumeSession(),
                t('controls.resumeError', 'Failed to resume session'),
              );
            }}
            isLoading={isPending}
          />
        )}

        {hasRounds && (
          <div className="w-full overflow-x-hidden">
            <RoundNavigation
              session={activeSessionTyped}
              currentRound={currentRound}
              selectedRound={displayRound}
              viewedRoundIndex={viewedRoundIndex}
              currentRoundIndex={currentRoundIndex}
              onRoundChange={(round) => {
                if (round.id === currentRound?.id) {
                  setSelectedRoundId(undefined);
                } else {
                  setSelectedRoundId(round.id);
                }
              }}
              onGoToCurrentRound={() => {
                setSelectedRoundId(undefined);
              }}
              onNextRound={handleNextRound}
              onCompleteRound={async () => {
                if (currentRound) {
                  await handleCompleteRound(currentRound.id);
                }
              }}
              isPaused={isPaused}
            />
          </div>
        )}

        <Collapsible
          className="flex flex-col items-end"
          open={isViewOptionsOpen}
          onOpenChange={setIsViewOptionsOpen}
        >
          <CollapsibleContent
            id={viewOptionsContentId}
            className="mt-2 flex flex-wrap items-center justify-end gap-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {t('active.sort.criteriaLabel', 'Sort')}
              </span>
              <ToggleGroup
                type="single"
                value={sortCriterion}
                onValueChange={(value) => {
                  if (!value) return;
                  const newCriterion = value as PlayerSortCriterion;
                  setSortCriterion(newCriterion);
                  // Set default direction based on criterion
                  if (newCriterion === 'score') {
                    setSortDirection('desc');
                  } else if (newCriterion === 'position') {
                    setSortDirection('asc');
                  }
                }}
                aria-label={t('active.sort.criteriaAria', 'Select sort criterion')}
                className="flex rounded-md border border-input p-1"
              >
                <ToggleGroupItem
                  value="position"
                  className="flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs sm:px-3"
                  aria-label={t('active.sort.position', 'Position')}
                >
                  <ListOrdered className="h-4 w-4" />
                  <span className="sr-only">{t('active.sort.position', 'Position')}</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="score"
                  className="flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs sm:px-3"
                  aria-label={t('active.sort.score', 'Score')}
                >
                  <Trophy className="h-4 w-4" />
                  <span className="sr-only">{t('active.sort.score', 'Score')}</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="flex items-center gap-1.5">
              <ToggleGroup
                type="single"
                value={sortDirection}
                onValueChange={(value) => {
                  if (!value) return;
                  setSortDirection(value as PlayerSortDirection);
                }}
                aria-label={t('active.sort.directionAria', 'Select sort direction')}
                className="flex rounded-md border border-input p-1"
              >
                <ToggleGroupItem
                  value="asc"
                  className="flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs sm:px-3"
                  aria-label={t('active.sort.directionAsc', 'Ascending')}
                >
                  <ArrowUpWideNarrow className="h-4 w-4" />
                  <span className="sr-only">{t('active.sort.directionAsc', 'Ascending')}</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="desc"
                  className="flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs sm:px-3"
                  aria-label={t('active.sort.directionDesc', 'Descending')}
                >
                  <ArrowDownWideNarrow className="h-4 w-4" />
                  <span className="sr-only">{t('active.sort.directionDesc', 'Descending')}</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={displayedPlayers.map((player) => player.id)}
            strategy={rectSortingStrategy}
          >
            <PlayersGrid
              displayedPlayers={displayedPlayers}
              activeSessionTyped={activeSessionTyped}
              displayRound={displayRound}
              viewedRoundIndex={viewedRoundIndex}
              currentRoundIndex={currentRoundIndex}
              selectedRoundId={selectedRoundId}
              currentRound={currentRound}
              onScoreChange={handleScoreChange}
              onScoreSet={handleScoreSet}
              onRemovePlayer={handleRemovePlayer}
              isPaused={isPaused}
              playerRankings={playerRankings}
              isDragEnabled={isDragEnabled}
            />
          </SortableContext>
        </DndContext>

        {isEndSessionOpen && (
          <EndSessionDialog
            open={isEndSessionOpen}
            onOpenChange={setIsEndSessionOpen}
            session={activeSessionTyped}
            onEnd={endSession}
            onSetWinner={setSessionWinner}
          />
        )}
      </motion.div>
      <SessionDetailsSheet
        open={isSessionDetailsOpen}
        onOpenChange={(open) => {
          setIsSessionDetailsOpen(open);
          if (!open) {
            setSessionDetailsInitialTab(undefined);
            setSessionDetailsInitialRoundId(undefined);
          }
        }}
        session={activeSessionTyped}
        initialTab={sessionDetailsInitialTab}
        initialRoundId={sessionDetailsInitialRoundId}
        onAddRoundNote={handleAddRoundNote}
        onAddRoundTag={handleAddRoundTag}
        onRemoveRoundTag={async (roundId: string, tag: string) => {
          const brandedRoundId = parseRoundId(roundId);
          const removeRoundTag = useSessionStore.getState().removeRoundTag;
          await handleAction(
            async () => removeRoundTag(brandedRoundId, tag),
            t('rounds.editTags.error', 'Failed to remove tag'),
          );
        }}
        onCompleteRound={async (roundId: string) => {
          await handleCompleteRound(roundId);
        }}
        onNextRound={handleNextRound}
        onScoreSet={handleScoreSet}
      />
    </ErrorBoundary>
  );
}
